/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable object-shorthand */
import { useCallback, useEffect, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Analytics from 'expo-firebase-analytics';
import { Linking, StyleSheet } from 'react-native';
import { v4 as uuidv4 } from 'uuid';
import SnapCalorieSitePage from '../../components/SnapCalorieSitePage';
import { SplitBox, TextWrap as Text, HelperText, View, TextInput, styles as sc } from '../../components/Themed';
import { RootStackScreenProps } from '../../navigation/navigationTypes';
// Image assets
import TextButton from '../../components/elements/TextButton';
import apiConst from '../../constants/Api';
import uploadSurveyResponsesUtil from '../../util';

/* 
TODO(wnorris): I think this page can be deleted since we now just direct to
Stripe from checkout, but leaving it here just in case.
*/

enum FormState {
  LOADING,
  INPUT,
  SUBMITTED,
  CONFIRM_DUPLICATE,
  VERIFIED,
  COMPLETE,
}

export default function EmailQuestion({ navigation, route }: RootStackScreenProps<'Email'>) {
  const [email, setEmail] = useState(route.params.existingEmail ?? '');
  const [isUpgrade, setIsUpgrade] = useState(route.params.isUpgrade ?? false);

  const [showFormErrors, setShowFormErrors] = useState(false);
  const [surveyResponses, setSurveyResponses] = useState({});
  const [formErrorText, setFormErrorText] = useState('');
  const [formState, setFormState] = useState(FormState.INPUT);

  const [originUrl, setOriginUrl] = useState<string | null>(null);

  useEffect(() => {
    Linking.getInitialURL().then((url) => {
      const parsedUrl = new URL(url ?? '');
      setOriginUrl(parsedUrl.origin);
    });
  }, []);

  const hasEmailErrors = () => {
    return !email.includes('@') || !email.includes('.') || email.split('.').length < 2;
  };

  const getSurveyResponses = useCallback(async () => {
    const tempResponses = JSON.parse((await AsyncStorage.getItem(`@snapcalorie_survey_responses`)) ?? '{}');
    if (typeof tempResponses.id !== 'string') {
      tempResponses.id = uuidv4();
    }
    setSurveyResponses(tempResponses);
    setFormState(FormState.INPUT);
  }, []);

  const uploadSurveyResponses = useCallback(async () => {
    const mergedResponse = { ...surveyResponses, emailAddress: email };
    uploadSurveyResponsesUtil(mergedResponse);
  }, [email, surveyResponses]);

  const goToStripeCheckout = useCallback(async () => {
    // Upload the survey responses
    await uploadSurveyResponses();

    const selectedSub = route?.params?.sub ?? 'monthly';
    await AsyncStorage.setItem('@snapcalorie_checkout_sub', selectedSub);
    const priceId = selectedSub === 'yearly' ? apiConst.yearlyPriceKey : apiConst.monthlyPriceKey;
    const reqUrl = `${apiConst.backendUrl}/checkout/session/${priceId}`;
    fetch(reqUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email,
        redirect: `${originUrl}/checkout-success`,
        promoCode: route?.params?.promoCode,
      }),
    }).then((resp) => {
      if (resp.status === 201) {
        resp.json().then((respJson) => {
          Analytics.logEvent('conversion-checkout');
          Analytics.logEvent(`conversion-checkout-${selectedSub}`);
          global.window.location.href = respJson.url;
        });
      } else {
        console.error(resp.statusText);
      }
    });
  }, [email, originUrl, route?.params?.promoCode, route?.params?.sub, uploadSurveyResponses]);

  const testIfEmailIsAvailable = useCallback(() => {
    const reqUrl = `${apiConst.backendUrl}/user/available?email=${encodeURIComponent(email)}`;
    fetch(reqUrl, {
      method: 'GET',
    }).then((resp) => {
      if (resp.status === 200) {
        resp.json().then((respJson) => {
          if (respJson as boolean) {
            setFormState(FormState.VERIFIED);
          } else {
            setFormState(FormState.CONFIRM_DUPLICATE);
          }
        });
      } else {
        setFormErrorText('Error communicating with the server. Please try again later.');
      }
    });
  }, [email]);

  useEffect(() => {
    switch (formState) {
      case FormState.LOADING:
        getSurveyResponses();
        break;
      case FormState.INPUT:
        break;
      case FormState.SUBMITTED:
        testIfEmailIsAvailable();
        break;
      case FormState.VERIFIED:
        goToStripeCheckout();
        break;
      default:
        break;
    }
  }, [formState, getSurveyResponses, goToStripeCheckout, testIfEmailIsAvailable]);

  const handleSubmit = () => {
    const storeEmailAndCheckout = async () => {
      try {
        await AsyncStorage.setItem('@snapcalorie_checkout_email_address', email);
      } catch (e) {
        console.error(e);
      }
      setFormState(FormState.SUBMITTED);
    };

    if (hasEmailErrors()) {
      setShowFormErrors(true);
    } else {
      storeEmailAndCheckout();
    }
  };

  return (
    <SnapCalorieSitePage
      headerLinks={{
        Home: () => {
          navigation.navigate('Home');
        },
      }}
    >
      <SplitBox
        flex={[1]}
        mobileView={false}
        style={{
          alignSelf: 'center',
        }}
        key="SplitBox"
      >
        <View
          style={{
            maxWidth: 300,
          }}
          key="ContainerView"
        >
          <Text style={[sc.h4, { textAlign: 'center', marginBottom: 20 }]}>Confirm email</Text>
          <Text style={styles.descriptionText}>
            You will use this email to login to the SnapCalorie app and receive receipts and other important {}
            communciation.
          </Text>
          <View style={{ height: 20 }} />

          {formState === FormState.LOADING && (
            <Text style={[styles.descriptionText, { marginTop: 60 }]}>Loading...</Text>
          )}
          {formState === FormState.CONFIRM_DUPLICATE && (
            <>
              <Text style={[sc.focusText, { marginTop: 60, textAlign: 'center' }]}>
                An account with the email {email} already exists. Continuing will upgrade the account to paid. {}
                If this is not your account please use a different email.
              </Text>
              <Text
                style={[sc.focusText, { textAlign: 'center', alignSelf: 'center' }]}
                onPress={() => {
                  setFormState(FormState.INPUT);
                }}
              >
                <u>Try a different email.</u>
              </Text>
              <TextButton
                onPress={() => {
                  setFormState(FormState.VERIFIED);
                }}
                text="CONFIRM"
                textStyle={sc.buttonText}
                style={[sc.buttonWrapper, { backgroundColor: '#2364AA' }]}
              />
            </>
          )}
          {formState === FormState.INPUT && (
            <>
              <TextInput
                placeholder="Email"
                value={email}
                onChangeText={(newEmail: string) => {
                  setEmail(newEmail.trim());
                }}
              />
              <HelperText style={showFormErrors ? sc.invalidText : sc.hintText} visible={hasEmailErrors()}>
                Please enter a valid email address.
              </HelperText>
              <TextButton
                onPress={() => {
                  handleSubmit();
                }}
                text="Continue"
                textStyle={sc.buttonText}
                style={[sc.buttonWrapper, { backgroundColor: '#2364AA' }]}
                disabled={hasEmailErrors()}
              />
              <HelperText
                style={showFormErrors ? sc.invalidText : sc.hintText}
                visible={!!formErrorText && formErrorText.length > 0}
              >
                {formErrorText}
              </HelperText>
            </>
          )}
          {formState === FormState.VERIFIED && (
            <Text style={[styles.descriptionText, { marginTop: 60 }]}>Sending you over to the checkout page!</Text>
          )}
        </View>
      </SplitBox>
    </SnapCalorieSitePage>
  );
}

const styles = StyleSheet.create({
  descriptionText: {
    ...sc.focusText,
    fontSize: 16,
    fontWeight: '700',
    textAlign: 'center',
    marginTop: 10,
  },
});
